import { createFileRoute, useRouter } from "@tanstack/react-router";

import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import DEVICE_CONTEXT from "src/context/internal/device";
import { DescriptionDetails, DescriptionList, DescriptionTerm } from "src/components/base/description-list";
import { Button } from "src/components/base/button";
import {
    ArrowDownTrayIcon,
    ArrowTopRightOnSquareIcon,
    ClipboardDocumentListIcon,
    PlusIcon,
    TrashIcon,
} from "@heroicons/react/20/solid";
import { toast } from "react-toastify";
import SETTINGS_CONTEXT from "src/context/internal/settings";
import { Subheading } from "src/components/base/heading";
import { Dialog, DialogActions, DialogDescription, DialogTitle } from "src/components/base/dialog";
import { SimpleTag } from "src/api/generated/frontend_internal_v1";
import { ComboboxOption, StatelessCombobox } from "src/components/base/combobox";
import { Field, Label } from "src/components/base/fieldset";
import CreateTagDialog from "src/components/dialogs/create-tag";
import { Badge } from "src/components/base/badges/base";
import ACCESS_RULE_CONTEXT from "src/context/internal/access-rule";
import { Text } from "src/components/base/text";
import { UUID } from "src/api/api_common";
import { ApiInternal } from "src/api/api_internal";
import { Divider } from "src/components/base/divider";
import DeleteExtraRecordDialog from "src/components/dialogs/delete-extra-record";
import { Link } from "src/components/base/link";
import { TagBadge } from "src/components/base/badges/tag";

/**
 * The properties for {@link GeneralDeviceView}
 */
export type GeneralDeviceViewProps = {};

/**
 * A view for a single device
 */
function GeneralDeviceView(props: GeneralDeviceViewProps) {
    const [t] = useTranslation("admin-device-view");
    const [tg] = useTranslation();

    const settingsContext = React.useContext(SETTINGS_CONTEXT);
    const deviceContext = React.useContext(DEVICE_CONTEXT);
    const accessRuleCtx = React.useContext(ACCESS_RULE_CONTEXT);

    const router = useRouter();

    const device = deviceContext.device;
    const settings = settingsContext.settings;
    const data = Route.useLoaderData();

    // Popups
    const [openRemoveDeviceTag, setOpenRemoveDeviceTag] = React.useState<SimpleTag>();
    const [openCreateTag, setOpenCreateTag] = React.useState(false);
    const [openDeleteExtraRecord, setOpenDeleteExtraRecord] = React.useState<UUID | undefined>(undefined);

    const [tagSearch, setTagSearch] = React.useState("");

    /**
     * Remove a tag from a device
     *
     * @param tag The tag to remove
     */
    const removeDeviceTag = async (tag: UUID) => {
        await ApiInternal.devices.removeDeviceTag(device.uuid, tag);
        deviceContext.reset();
    };

    /**
     * Add a tag to a device
     *
     * @param tag The tag to add
     */
    const addDeviceTag = async (tag: UUID) => {
        await ApiInternal.devices.addDeviceTag(device.uuid, tag);
        deviceContext.reset();
    };

    /**
     * Api call do get the device's tailscale state and open it in Json format in a new tab
     */
    const dumpDeviceState = async () => {
        const result = await ApiInternal.devices.dumpDeviceState(device.uuid);
        const blob = new Blob([JSON.stringify(result)], { type: "application/javascript" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
    };

    /**
     * Api call do get the device's tailscale state and download it as a json
     */
    const downloadDeviceState = async () => {
        const result = await ApiInternal.devices.dumpDeviceState(device.uuid);
        if (result.isErr) {
            toast.error(result.err.message);
            return;
        }
        const json = result.ok;
        if (json === null || json === undefined) {
            toast.error("Not Found");
            return;
        }

        downloadBlob(
            new Blob([JSON.stringify(json)], { type: "application/javascript" }),
            device.dnsname + "-targets.json",
        );
    };

    let filteredTags = device.tags
        ? accessRuleCtx.options.tags.filter((tag) => device.tags && !device.tags.some((at) => at.name === tag.name))
        : accessRuleCtx.options.tags;
    filteredTags =
        tagSearch === ""
            ? filteredTags
            : filteredTags.filter((x) => x.name.toLowerCase().includes(tagSearch.toLowerCase()));

    return (
        <div>
            <div className={"flex w-full justify-between"}>
                <Subheading>{t("heading.data")}</Subheading>
                <div className={"flex items-center gap-2"}>
                    <Text>{t("button.dump-state")}: </Text>
                    <Button plain={true} onClick={() => dumpDeviceState()}>
                        <ArrowTopRightOnSquareIcon />
                    </Button>
                    <Button plain={true} onClick={() => downloadDeviceState()}>
                        <ArrowDownTrayIcon />
                    </Button>
                </div>
            </div>
            <div className={"max-w-xl"}>
                <DescriptionList>
                    {device.user && (
                        <>
                            <DescriptionTerm>{t("label.owner")}</DescriptionTerm>
                            <DescriptionDetails>
                                <Button
                                    className={"-translate-x-3"}
                                    plain={true}
                                    href={"/a/users/$userId/general"}
                                    params={{ userId: device.user.uuid }}
                                >
                                    {device.user.display_name}
                                    <ArrowTopRightOnSquareIcon />
                                </Button>
                            </DescriptionDetails>
                        </>
                    )}

                    <DescriptionTerm>{t("label.dns-names")}</DescriptionTerm>
                    <DescriptionDetails className={"flex flex-col"}>
                        {[device.dnsname, ...settings.headscale_domains.map((d) => `${device.dnsname}.${d}`)].map(
                            (name, idx) => (
                                <span key={idx}>{name}</span>
                            ),
                        )}
                        {data.list.length > 0 && (
                            <>
                                <Divider className={"my-1"} />
                                {data.list.map((dns) => (
                                    <div className={"flex justify-between"} key={dns.uuid}>
                                        <Link
                                            className={"flex items-center gap-2 hover:cursor-pointer"}
                                            href={"/a/dns/extra-records"}
                                            search={{ search: dns.from_dns }}
                                        >
                                            <Badge color={"blue"} className={"w-fit"}>
                                                {t("label.extra-dns")}
                                            </Badge>
                                            <span className={"w-50 truncate"}>{dns.from_dns}</span>
                                        </Link>
                                        <Button
                                            plain={true}
                                            onClick={() => {
                                                const record_uuid = dns.records.find(
                                                    (r) => r.device_uuid === device.uuid,
                                                )?.uuid;
                                                if (!record_uuid) return;
                                                setOpenDeleteExtraRecord(record_uuid);
                                            }}
                                            className={"w-fit"}
                                        >
                                            <TrashIcon />
                                        </Button>
                                    </div>
                                ))}
                            </>
                        )}
                    </DescriptionDetails>

                    {device.ipv4 && (
                        <>
                            <DescriptionTerm>{t("label.ipv4")}</DescriptionTerm>
                            <DescriptionDetails className={"flex items-center gap-1"}>
                                <span>{device.ipv4}</span>
                                <Button
                                    plain={true}
                                    onClick={async () => {
                                        device.ipv4 && (await navigator.clipboard.writeText(device.ipv4));
                                        toast.success(tg("toast.copied-to-clipboard"));
                                    }}
                                >
                                    <ClipboardDocumentListIcon />
                                </Button>
                            </DescriptionDetails>
                        </>
                    )}

                    {device.ipv6 && (
                        <>
                            <DescriptionTerm>{t("label.ipv6")}</DescriptionTerm>
                            <DescriptionDetails className={"flex items-center gap-1"}>
                                <span>{device.ipv6}</span>
                                <Button
                                    plain={true}
                                    onClick={async () => {
                                        device.ipv6 && (await navigator.clipboard.writeText(device.ipv6));
                                        toast.success(tg("toast.copied-to-clipboard"));
                                    }}
                                >
                                    <ClipboardDocumentListIcon />
                                </Button>
                            </DescriptionDetails>
                        </>
                    )}

                    <DescriptionTerm>{t("label.hostname")}</DescriptionTerm>
                    <DescriptionDetails>{device.hostname}</DescriptionDetails>

                    <DescriptionTerm>{t("label.first-seen")}</DescriptionTerm>
                    <DescriptionDetails>{new Date(device.created_at).toLocaleDateString("de-de")}</DescriptionDetails>

                    <DescriptionTerm>{t("label.last-seen")}</DescriptionTerm>
                    <DescriptionDetails>
                        {device.online ? (
                            <div className={"flex items-center gap-2"}>
                                <div className={"size-2 animate-pulse rounded bg-green-500"} />
                                <span>{t("label.online")}</span>
                            </div>
                        ) : device.last_seen ? (
                            new Date(device.last_seen).toLocaleString("de-de")
                        ) : (
                            "?"
                        )}
                    </DescriptionDetails>

                    <DescriptionTerm>{t("label.expiry")}</DescriptionTerm>
                    <DescriptionDetails>
                        {device.expiry ? new Date(device.expiry).toLocaleDateString("de-de") : "-"}
                    </DescriptionDetails>

                    {device.tags && (
                        <>
                            <DescriptionTerm>{t("label.tags")}</DescriptionTerm>
                            <DescriptionDetails className={"flex flex-wrap gap-1"}>
                                {device.tags.map((tag) => (
                                    <TagBadge
                                        key={tag.uuid}
                                        tag={tag}
                                        className={"flex !gap-1"}
                                        onRemove={() => setOpenRemoveDeviceTag(tag)}
                                    />
                                ))}
                            </DescriptionDetails>
                        </>
                    )}
                </DescriptionList>

                {device.tags && (
                    <Field className={"mt-3"}>
                        <Label>{t("label.add-tags")}</Label>
                        <div className={"mt-2 flex gap-3"}>
                            <StatelessCombobox<SimpleTag | null>
                                onChange={async (e) => {
                                    if (!e) return;
                                    await addDeviceTag(e.uuid);
                                }}
                                onClose={() => setTagSearch("")}
                                onQueryChange={(q) => setTagSearch(q)}
                            >
                                {filteredTags.map((tag) => (
                                    <ComboboxOption value={tag} key={tag.uuid}>
                                        <Label>{`tag:${tag.name}`}</Label>
                                    </ComboboxOption>
                                ))}
                            </StatelessCombobox>

                            <Button className={"w-full max-w-fit self-center"} onClick={() => setOpenCreateTag(true)}>
                                <PlusIcon />
                                {t("button.create-tag")}
                            </Button>
                        </div>
                    </Field>
                )}

                {openCreateTag && (
                    <Suspense>
                        <CreateTagDialog onClose={() => setOpenCreateTag(false)} />
                    </Suspense>
                )}
                {openRemoveDeviceTag && (
                    <Suspense>
                        <Dialog open={true} onClose={() => setOpenRemoveDeviceTag(undefined)}>
                            <DialogTitle>{t("heading.remove-tag", { tag: openRemoveDeviceTag.name })}</DialogTitle>
                            <DialogDescription>{t("description.remove-tag")}</DialogDescription>
                            <DialogActions>
                                <Button onClick={() => setOpenRemoveDeviceTag(undefined)}>{tg("button.cancel")}</Button>
                                <Button
                                    color={"red"}
                                    onClick={async () => {
                                        await removeDeviceTag(openRemoveDeviceTag?.uuid);
                                        setOpenRemoveDeviceTag(undefined);
                                    }}
                                >
                                    {t("button.remove-tag")}
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </Suspense>
                )}
                {openDeleteExtraRecord && (
                    <Suspense>
                        <DeleteExtraRecordDialog
                            record={openDeleteExtraRecord}
                            onClose={() => setOpenDeleteExtraRecord(undefined)}
                            onDelete={() => {
                                setOpenDeleteExtraRecord(undefined);
                                router.invalidate();
                            }}
                        />
                    </Suspense>
                )}
            </div>
        </div>
    );
}

/**
 * Download a blob
 *
 * @param blob the blob to download
 * @param filename Filename to set
 */
function downloadBlob(blob: Blob, filename: string) {
    const a = document.createElement("a");
    const url = URL.createObjectURL(blob);
    a.setAttribute("href", url);
    a.setAttribute("download", filename);
    a.click();
    URL.revokeObjectURL(url);
}

export const Route = createFileRoute("/_admin/a/devices/$deviceId/_device/general")({
    component: GeneralDeviceView,
    loader: async ({ params }) => await ApiInternal.dns.getDeviceExtraDns(params.deviceId),
});
